import React, { useCallback, useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../../ui/sheet';
import { X, Copy, Check, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { toast } from 'sonner';
import { aiAgentApi } from '../../../lib/aiAgentApi';
import api from '../../../lib/api';
import { getCurrentAccount, isSuperAdmin } from '../../../lib/auth';
import { LeftRail } from './LeftRail';
import { ChatsView } from './ChatsView';
import { EmailView } from './EmailView';
import { DriveView } from './DriveView';
import { AgentSparkleAvatar } from './AgentSparkleAvatar';
import { WalletBadge } from './WalletBadge';
import TopUpDialog from '../../credits/TopUpDialog';

// Credit gating thresholds (USD). Balance shown to the user as CREDITS.
const LOW_BALANCE_USD = 3.0;     // < $3  → amber "running low" tone + tooltip
const BLOCK_BALANCE_USD = 0.10;  // < $0.10 (1 credit) → freeze send until top-up

/**
 * AI Agent drawer v2 — mobile/iPad-style three-tab shell.
 * Width: 75vw on desktop, full-width on small screens.
 */
export const AiAgentDrawerV2 = ({ open, onOpenChange, me }) => {
  const [tab, setTab] = useState('chats');
  const [emailTab, setEmailTab] = useState('inbox');
  const [badges, setBadges] = useState({});
  // Resume payload from the /credits/success page (or any external caller
  // that wants the drawer to auto-open a specific chat + prime a message).
  const [resume, setResume] = useState(null); // { conv_id, prompt }

  // ── AI-credit wallet (powers Astrid) ────────────────────────────────────
  // The balance display + composer gating both read from here. Super Admin /
  // Owner are exempt from gating (they never spend credits).
  const [wallet, setWallet] = useState(null);
  const [topupOpen, setTopupOpen] = useState(false);
  const account = getCurrentAccount();
  const staff = isSuperAdmin();

  const loadWallet = useCallback(() => {
    if (!account?.id) return;
    api.get('/credits/wallet', { params: { account_id: account.id } })
      .then(({ data }) => setWallet(data))
      .catch(() => { /* non-fatal — leave last-known balance */ });
  }, [account?.id]);

  // Keep the balance fresh while the drawer is open (poll + on window focus).
  useEffect(() => {
    if (!open) return;
    loadWallet();
    const iv = setInterval(loadWallet, 45000);
    const onFocus = () => loadWallet();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); };
  }, [open, loadWallet]);

  const balUsd = wallet?.balance_usd ?? 0;
  const blocked = !staff && !!wallet && balUsd < BLOCK_BALANCE_USD;
  const low = !staff && !!wallet && !blocked && balUsd < LOW_BALANCE_USD;

  // Listen for in-app "open drawer with tab" events. The MessageBubble
  // dispatches this when the agent's text contains an Approvals-queue link —
  // we route the user into Email → Approvals instead of a broken URL.
  useEffect(() => {
    const handler = (evt) => {
      const detail = (evt && evt.detail) || {};
      if (detail.tab) setTab(detail.tab);
      if (detail.emailTab) setEmailTab(detail.emailTab);
      if (detail.conv_id) {
        setTab('chats');
        setResume({ conv_id: detail.conv_id, prompt: detail.prompt || null });
      }
      // Caller (header bell etc.) usually wants the drawer visible too.
      if (!open) onOpenChange?.(true);
    };
    window.addEventListener('ai-agent:open', handler);
    return () => window.removeEventListener('ai-agent:open', handler);
  }, [open, onOpenChange]);

  // Poll the email approvals count so a P0 vendor-extension batch surfaces.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const r = await aiAgentApi.listEmails('all', { limit: 1 });
        if (cancelled) return;
        const c = r.counts || {};
        setBadges({
          email: (c.approvals || 0) + (c.inbox || 0) + (c.failed || 0),
        });
      } catch { /* non-fatal */ }
    };
    fetchCounts();
    const iv = setInterval(fetchCounts, 20_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [open]);

  const personaName = me?.persona_name || 'Astrid';
  const agentEmail = me?.agent_email || '';
  const [copied, setCopied] = useState(false);

  // Stash the agent name in localStorage so other components (chat
  // bubbles, etc.) can render the avatar without prop-drilling through
  // ChatsView → ChatDetail → MessageList.
  useEffect(() => {
    if (personaName) {
      try { localStorage.setItem('ai_agent_persona_name', personaName); } catch { /* ignore */ }
    }
  }, [personaName]);

  const copyAgentEmail = async () => {
    if (!agentEmail) return;
    try {
      await navigator.clipboard.writeText(agentEmail);
      setCopied(true);
      toast.success('Agent email copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="p-0 flex flex-col w-screen sm:max-w-none"
        style={{ width: 'min(75vw, 1280px)' }}
        data-testid="ai-agent-drawer-v2"
        hideCloseButton
      >
        {/* Visually-hidden title + description for Radix a11y */}
        <SheetTitle className="sr-only">{personaName} — AI Agent</SheetTitle>
        <SheetDescription className="sr-only">
          Your AI assistant for BillPortal. Switch between Chats, Email, and Drive using the left rail.
        </SheetDescription>

        {/* Header (matches wireframe: {Ai Agent Name} ........ X) */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <AgentSparkleAvatar name={personaName} size={32} />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-sm font-semibold text-slate-900 truncate" data-testid="ai-agent-persona-name">{personaName}</div>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600">AI Agent</span>
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-200 hover:bg-amber-100"
                        data-testid="ai-agent-beta-badge"
                        aria-label="Beta — read disclaimer"
                      >
                        (Beta)<Info className="h-2.5 w-2.5 ml-0.5 opacity-80" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      align="start"
                      className="max-w-[340px] text-[11px] leading-[15px] p-3"
                      data-testid="ai-agent-beta-tooltip"
                    >
                      <p className="font-semibold text-foreground mb-1.5">{personaName} is in Beta</p>
                      <p className="text-muted-foreground">
                        Responses and reports generated by the agent are <strong className="text-foreground">AI-assisted</strong>{' '}
                        and may contain inaccuracies. <strong className="text-foreground">Please review and confirm</strong>{' '}
                        every output — including dates, amounts, account numbers, and commitments — before sharing
                        with vendors, stakeholders, or anyone outside your team.
                      </p>
                      <p className="text-muted-foreground mt-1.5">
                        The agent acts on the instructions <em>you</em> give it. BillPortal Technologies, Inc. has no
                        control over user-supplied prompts or the resulting outputs and therefore{' '}
                        <strong className="text-foreground">accepts no liability</strong> for any consequence arising
                        from the use, sharing, or implementation of agent-generated content. You remain the responsible
                        approver of record on every drafted communication.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {agentEmail && (
                <button
                  data-testid="ai-agent-email-copy"
                  onClick={copyAgentEmail}
                  className="group mt-0.5 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-emerald-700"
                  title="Copy agent email"
                >
                  <span className="truncate max-w-[280px]">{agentEmail}</span>
                  {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3 opacity-60 group-hover:opacity-100" />}
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <WalletBadge
              wallet={wallet}
              staff={staff}
              low={low}
              blocked={blocked}
              personaName={personaName}
              onTopUp={() => setTopupOpen(true)}
            />
            <button
              data-testid="ai-agent-close"
              onClick={() => onOpenChange(false)}
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body: left rail + active view */}
        <div className="flex min-h-0 flex-1">
          <LeftRail active={tab} onChange={setTab} badges={badges} />
          <div className="min-w-0 flex-1">
            {tab === 'chats' && (
              <ChatsView
                resume={resume}
                onResumeConsumed={() => setResume(null)}
                blocked={blocked}
                personaName={personaName}
                onTopUp={() => setTopupOpen(true)}
                refreshWallet={loadWallet}
              />
            )}
            {tab === 'email' && <EmailView initialTab={emailTab} />}
            {tab === 'drive' && <DriveView />}
          </div>
        </div>

        <TopUpDialog
          open={topupOpen}
          onOpenChange={setTopupOpen}
          wallet={wallet}
          onOpened={loadWallet}
        />
      </SheetContent>
    </Sheet>
  );
};

export default AiAgentDrawerV2;
